/**
 * Supabase Client
 *
 * Re-exports the Supabase client from integrations.
 * Handles authentication with Supabase Auth.
 */

import { supabase } from '@/integrations/supabase/client';

// Re-export supabase client
export { supabase };

// Email domain restriction - multiple domains allowed
export const ALLOWED_EMAIL_DOMAINS = ['get-creative.co', 'intentionallycreative.com'];
export const ALLOWED_EMAIL_DOMAIN = ALLOWED_EMAIL_DOMAINS[0]; // Primary domain for display

/**
 * Check if an email is from an allowed domain
 */
export function isAllowedEmail(email: string): boolean {
  const emailLower = email.toLowerCase();
  return ALLOWED_EMAIL_DOMAINS.some(domain => emailLower.endsWith(`@${domain.toLowerCase()}`));
}

/**
 * Get the current session's access token
 */
export async function getAccessToken(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Sign in with Google OAuth
 *
 * On web: Uses standard OAuth redirect flow via Supabase
 * On native (Capacitor): Uses native Google Sign-In SDK to get an ID token,
 * then exchanges it with Supabase via signInWithIdToken()
 */
export async function signInWithGoogle() {
  // Web OAuth redirect flow
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: window.location.origin,
    },
  });

  if (error) throw error;
  return data;
}

/**
 * Sign out
 */
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

/**
 * Get current user
 */
export async function getCurrentUser() {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
}

/**
 * Listen for auth state changes
 */
export function onAuthStateChange(callback: (event: string, session: any) => void) {
  return supabase.auth.onAuthStateChange(callback);
}
